import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { PasswordField } from '@/components/password-field';
import { TextField } from '@/components/text-field';
import type { Copy } from '@/lib/messages';

interface PasswordStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  initialEmail: string;
  onSubmit: (email: string, password: string) => void;
  onUseCode: () => void;
}

/** The fallback branch for people who cannot reach their inbox (ADR 0007). */
export function PasswordStep({
  copy,
  pending,
  error,
  initialEmail,
  onSubmit,
  onUseCode,
}: PasswordStepProps) {
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (email.trim() && password) onSubmit(email.trim(), password);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}
      <TextField
        id="password-email"
        label={copy.emailLabel}
        type="email"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        autoComplete="email"
        inputMode="email"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={copy.emailPlaceholder}
        // Focus whichever field is actually empty: arriving from the email
        // step the address is already filled in, and stealing focus back to
        // it would make the user tab past what they came here to type.
        autoFocus={!initialEmail}
        required
      />
      <PasswordField
        id="password"
        label={copy.passwordLabel}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        showLabel={copy.passwordShow}
        hideLabel={copy.passwordHide}
        autoFocus={Boolean(initialEmail)}
        required
      />
      <Button
        type="submit"
        loading={pending}
        disabled={!email.trim() || !password}
      >
        {copy.passwordSubmit}
      </Button>

      {/* Both ways out of this screen, in one group. Separated from the form
          so neither reads as part of signing in. */}
      <div className="mt-7 flex flex-col items-center gap-2 border-t border-buds-dark/10 pt-5">
        <Button
          type="button"
          variant="link"
          onClick={onUseCode}
          disabled={pending}
        >
          {copy.useCodeInstead}
        </Button>
        {/* A full navigation, not an in-flow step: recovery lives outside the
            interaction, and the user will sign in fresh afterwards
            (docs/auth/40-features/password-reset.md). */}
        <a
          href="/forgot-password"
          className="focus-ring py-1 text-sm font-semibold text-buds-dark underline underline-offset-4 hover:text-buds-dark/70"
        >
          {copy.forgotLink}
        </a>
      </div>
    </form>
  );
}
